/**
 * Reloads the top `count` rows of a keyset-paged list in pages of at most
 * `maxPage` rows — master caps a page at 200. A refresh after a realtime
 * frame keeps every row the user has paged to instead of cutting the list
 * back to one page (and the selection on the rows past it) (I-2218).
 *
 * `page(limit, after)` loads up to `limit` rows after the row `after` (the
 * first page without). The result says whether rows beyond the range exist:
 * a short page ends the list.
 */
export async function fetchLoadedRange<Row extends { id: number }>(
  count: number,
  maxPage: number,
  page: (limit: number, after: Row | undefined) => Promise<Row[]>,
): Promise<{ rows: Row[]; more: boolean }> {
  const rows: Row[] = [];
  const seen = new Set<number>();
  let after: Row | undefined;
  for (;;) {
    const limit = Math.min(maxPage, Math.max(1, count - rows.length));
    const got = await page(limit, after);
    // A row that moved up between two pages could come twice.
    for (const row of got) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    if (got.length < limit) return { rows, more: false };
    if (rows.length >= count) return { rows, more: true };
    after = got[got.length - 1];
  }
}
